import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { giveAskApi, type GiveAskDirectoryItem, type GiveAskFilters } from '@/lib/give-ask-api';

export function useGivesAsks(communityId: string, filters: GiveAskFilters) {
  const filterKey = JSON.stringify(filters);
  const stableFilters = useMemo<GiveAskFilters>(() => JSON.parse(filterKey) as GiveAskFilters, [filterKey]);

  const [items, setItems] = useState<GiveAskDirectoryItem[]>([]);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const requestRef = useRef(0);

  const fetchPage = useCallback(
    async (targetPage: number, replace: boolean) => {
      const requestId = ++requestRef.current;
      if (replace) setIsLoading(true);
      else setIsLoadingMore(true);
      try {
        const data = await giveAskApi.directory(communityId, { ...stableFilters, page: targetPage });
        if (requestId !== requestRef.current) return;
        setItems((prev) => (replace ? data.items : [...prev, ...data.items]));
        setCurrentUserId(data.currentUserId);
        setPage(targetPage);
        setHasMore(data.hasMore);
        setError(null);
      } catch {
        if (requestId === requestRef.current) setError('Could not load gives and asks');
      } finally {
        if (requestId === requestRef.current) {
          setIsLoading(false);
          setIsLoadingMore(false);
        }
      }
    },
    [communityId, stableFilters]
  );

  useEffect(() => {
    void fetchPage(1, true);
  }, [fetchPage]);

  const loadMore = useCallback(() => {
    if (!hasMore || isLoadingMore) return;
    void fetchPage(page + 1, false);
  }, [fetchPage, hasMore, isLoadingMore, page]);

  const updateItem = useCallback((id: string, patch: Partial<GiveAskDirectoryItem>) => {
    setItems((prev) => prev.map((item) => (item.id === id ? { ...item, ...patch } : item)));
  }, []);

  const reload = useCallback(() => fetchPage(1, true), [fetchPage]);

  return { items, currentUserId, hasMore, isLoading, isLoadingMore, error, loadMore, updateItem, reload };
}
